import { Box, Button, Flex, MenuButton, SimpleGrid } from '@chakra-ui/react';

import { CheckCircle } from 'assets/assetsV1/iconComponents';
import { ColorLens } from 'assets/assetsV1/iconComponents/ColorLens';

import { GRID_COLORS_KEYS, GRID_COLORS_LABELS, GRID_COLORS_MAP } from 'constants/ProjectConstants';

// import { useState } from 'react';
import { InputV2 } from 'uiToolkitV1/InputV2';
import { Menu, MenuItem, MenuList } from 'uiToolkitV1/MenuV2';
import { Tooltip } from 'uiToolkitV1/Tooltip';
import { TextBodyMedium } from 'uiToolkitV1/Typography';

export interface GridCell {
  color: string;
  label: string;
  checked?: boolean;
}

export interface CalibrationGridProps {
  gridDefinition: GridCell[][];
  onChange?: (rowNumber: number, colNumber: number, key: string, value: string) => void;
  isEditable?: boolean;
  isSelectable?: boolean;
}

const SelectableCell = ({
  cellIndex,
  cell,
  rowHeight,
  handleSelect,
}: {
  cellIndex: number;
  cell: GridCell;
  rowHeight: number;
  handleSelect: (index: number, checked: boolean) => void;
}) => {
  const { bg, text } = GRID_COLORS_MAP[cell.color];

  const bgColor = cell.checked ? text : bg;
  const textColor = cell.checked ? bg : text;

  return (
    <Button
      height={`${rowHeight}px`}
      bgColor={bgColor}
      color={textColor}
      fontWeight="600"
      fontSize="14px"
      borderRadius={0}
      onClick={() => handleSelect(cellIndex, !cell.checked)}
      _active={{
        bgColor,
      }}
      _hover={{
        bgColor,
      }}
      pos="relative"
    >
      <InputV2
        textAlign="center"
        isDashed
        isDisabled
        cursor="pointer !important"
        opacity="1 !important"
        value={cell.label}
        _hover={{
          borderBottomStyle: 'dashed !important',
        }}
      />
      {cell.checked && (
        <Box position="absolute" right="5%">
          <CheckCircle boxSize="24px" color="white" />
        </Box>
      )}
    </Button>
  );
};

const EditableCell = ({
  cellIndex,
  cell,
  rowHeight,
  handleChange,
  cellWrapperProps,
}: {
  cellIndex: number;
  cell: GridCell;
  rowHeight: number;
  handleChange: (index: number, key: 'color' | 'label', value: string) => void;
  cellWrapperProps: any;
}) => {
  const { bg, text } = GRID_COLORS_MAP[cell.color];

  const bgColor = bg;
  const textColor = text;

  return (
    <Button
      height={`${rowHeight}px`}
      bgColor={bgColor}
      color={textColor}
      fontWeight="600"
      fontSize="14px"
      borderRadius={0}
      _active={{
        bgColor,
      }}
      _hover={{
        bgColor,
      }}
      pos="relative"
      {...cellWrapperProps}
    >
      <Tooltip label={cell.label}>
        <InputV2
          textAlign="center"
          isDashed
          value={cell.label}
          onChange={(e) => handleChange(cellIndex, 'label', e.target.value)}
          fontSize="12px !important"
        />
      </Tooltip>
      <Menu>
        <MenuButton pos="absolute" top="8px" right="8px">
          <ColorLens boxSize="24px" color="text.g2" />
        </MenuButton>
        <MenuList minW="150px" p={2} borderRadius="8px" overflow="hidden">
          {GRID_COLORS_KEYS.map((key, j) => (
            <MenuItem key={key} isSelected={cell.color === key} onClick={() => handleChange(cellIndex, 'color', key)}>
              <Flex alignItems="center">
                <Box borderRadius="50%" border="1px solid #999" h="10px" w="10px" bgColor={GRID_COLORS_MAP[key].bg} />
                <TextBodyMedium ml="13px">{GRID_COLORS_LABELS[j]}</TextBodyMedium>
              </Flex>
            </MenuItem>
          ))}
        </MenuList>
      </Menu>
    </Button>
  );
};

const StaticCell = ({ cell, rowHeight }: { cellIndex: number; cell: GridCell; rowHeight: number }) => {
  const { bg, text } = GRID_COLORS_MAP[cell.color];

  const bgColor = cell.checked ? text : bg;
  const textColor = cell.checked ? bg : text;

  return (
    <Button
      height={`${rowHeight}px`}
      bgColor={bgColor}
      color={textColor}
      fontWeight="600"
      fontSize="14px"
      borderRadius={0}
      _active={{
        bgColor,
      }}
      _hover={{
        bgColor,
      }}
      pos="relative"
    >
      <InputV2
        textAlign="center"
        isDashed
        isDisabled
        cursor="pointer !important"
        opacity="1 !important"
        _hover={{
          borderBottomStyle: 'dashed !important',
        }}
        value={cell.label}
      />
      {cell.checked && (
        <Box position="absolute" right="5%">
          <CheckCircle boxSize="24px" color="white" />
        </Box>
      )}
    </Button>
  );
};

export const CalibrationGrid = ({
  gridDefinition,
  // gridDefinition: defaultGrid,
  onChange,
  isEditable,
  isSelectable,
}: CalibrationGridProps) => {
  // const [gridDefinition, setGridDefinition] = useState(defaultGrid);
  const columns = gridDefinition[0]?.length;

  const rowHeight = Math.max(288 / gridDefinition.length, 96);

  if (!columns) return <></>;

  const rows = gridDefinition.flat();

  const handleChange = (index: number, key: 'color' | 'label', value: string) => {
    const rowNumber = Math.floor(index / columns);
    const colNumber = index % columns;

    // const newGrid = [...gridDefinition];
    // newGrid[rowNumber][colNumber][key] = value;

    // setGridDefinition(newGrid);
    onChange(rowNumber, colNumber, key, value);
  };

  const handleSelect = (index: number, checked: boolean) => {
    // const rowNumber = Math.floor(index / columns);
    // const colNumber = index % columns;
    // const newGrid = [...gridDefinition];
    // newGrid[rowNumber][colNumber].checked = checked;
    // // setGridDefinition(newGrid);
    // onChange?.(newGrid);
  };

  let Cell = StaticCell;
  let cellProps: { handleChange?: typeof handleChange; handleSelect?: typeof handleSelect } = {};

  if (isEditable) {
    Cell = EditableCell;
    cellProps = { handleChange };
  }

  if (isSelectable) {
    Cell = SelectableCell;
    cellProps = { handleSelect };
  }

  return (
    <SimpleGrid columns={columns} gridGap="1px">
      {rows.map((row, i) => (
        <Cell
          cell={row}
          cellIndex={i}
          rowHeight={rowHeight}
          {...cellProps}
          cellWrapperProps={{
            borderTopLeftRadius: i === 0 ? '8px' : '0px',
            borderTopRightRadius: i === columns - 1 ? '8px' : '0px',
            borderBottomLeftRadius: i === columns * (gridDefinition.length - 1) ? '8px' : '0px',
            borderBottomRightRadius: i === columns * gridDefinition.length - 1 ? '8px' : '0px',
          }}
        />
      ))}
    </SimpleGrid>
  );
};
